import { keyframes, styled } from "@mui/material/styles";
import { THINKING_TUCK } from "./geometry";
// How each state moves. Lines have pathLength 1, so dash lengths and offsets
// are fractions of the line. index.html repeats the loading state for the boot
// screen, before React loads.
const EXPO = "cubic-bezier(0.16, 1, 0.3, 1)";
const IN_OUT = "cubic-bezier(0.65, 0, 0.35, 1)";
const OUT = "cubic-bezier(0.33, 1, 0.68, 1)";
const SINE_OUT = "cubic-bezier(0.61, 1, 0.88, 1)";
const SINE_IN = "cubic-bezier(0.12, 0, 0.39, 0)";
const LOADING_LEG = `1.1s ${IN_OUT} infinite alternate`;
const WRITING_CYCLE = `2.6s ${EXPO} infinite`;
const SYNCING_CYCLE = `1.5s ${IN_OUT} infinite`;
const THINKING_CYCLE = "1.6s";
const PONDERING_CYCLE = "1.8s";
const ONE_SHOT = `1s ${EXPO} both`;
// loading: a stroke shuttles between the dots, parked just out of each one so
// a stub shows while it turns round, and the dot it lands on swells.
const shuttle = keyframes`
  from { stroke-dashoffset: 0.15; }
  to { stroke-dashoffset: -0.75; }
`;
const landLeft = keyframes`
  from { transform: scale(1.25); }
  25%, to { transform: scale(1); }
`;
const landRight = keyframes`
  from, 75% { transform: scale(1); }
  to { transform: scale(1.25); }
`;
// thinking: the mark turns half a turn in the first half of the cycle and rests
// for the second. The mark is symmetrical, so each rest looks like the start.
// The dots tuck in towards the centre as it turns, like a skater.
const turn = keyframes`
  from { transform: rotate(0deg); }
  50%, to { transform: rotate(180deg); }
`;
const tuck = keyframes`
  0% { transform: translateX(0); animation-timing-function: ${SINE_OUT}; }
  25% { transform: translateX(var(--tuck)); animation-timing-function: ${SINE_IN}; }
  50%, 100% { transform: translateX(0); }
`;
const tuckLine = keyframes`
  0% { transform: scaleX(1); animation-timing-function: ${SINE_OUT}; }
  25% { transform: scaleX(${1 - THINKING_TUCK}); animation-timing-function: ${SINE_IN}; }
  50%, 100% { transform: scaleX(1); }
`;
// pondering: one stroke at a time drops onto the line, from above then below,
// and shrinks into it as it lands. Each stroke moves in the first half of the
// cycle; the other stroke runs half a cycle behind.
const fall = keyframes`
  from { transform: translateY(var(--from)); }
  50%, to { transform: translateY(0); }
`;
const absorb = keyframes`
  0% { opacity: 0; transform: scaleX(1); }
  12.5%, 35% { opacity: 0.85; }
  30% { transform: scaleX(1); }
  50%, 100% { opacity: 0; transform: scaleX(0); }
`;
// reduced motion: moving states breathe instead.
const breathe = keyframes`
  from { opacity: 0.55; }
  to { opacity: 1; }
`;
// writing: three words written left to right, held, then lifted off together.
const writeWord = (from, to)=>keyframes`
  0%, ${from}% { stroke-dashoffset: 1; opacity: 1; }
  ${to}%, 72% { stroke-dashoffset: 0; opacity: 1; }
  86%, 100% { stroke-dashoffset: 0; opacity: 0; }
`;
const wordWritten = keyframes`
  0%, 42%, 58%, 100% { transform: scale(1); }
  48% { transform: scale(1.25); }
`;
// reading: an underline steps along the line a phrase at a time, then returns
// to the start of the next line, like an eye reading a page.
const readLine = keyframes`
  0%, 14% { stroke-dashoffset: -0.06; opacity: 1; }
  20%, 36% { stroke-dashoffset: -0.3; }
  42%, 58% { stroke-dashoffset: -0.54; }
  64%, 82% { stroke-dashoffset: -0.78; opacity: 1; }
  90% { stroke-dashoffset: -0.78; opacity: 0; }
  91%, 100% { stroke-dashoffset: -0.06; opacity: 0; }
`;
// syncing: two strokes pass each other; both dots answer as they arrive.
const pass = keyframes`
  from { stroke-dashoffset: 0.3; }
  to { stroke-dashoffset: -1; }
`;
const receive = keyframes`
  0%, 78%, 100% { transform: scale(1); }
  90% { transform: scale(1.25); }
`;
// searching: a lens reads along the line and rings each dot it reaches.
const scan = keyframes`
  0%, 8% { transform: translateX(0); }
  92%, 100% { transform: translateX(var(--travel)); }
`;
// queued: a dotted line drifts slowly; nothing has started.
const drift = keyframes`
  to { stroke-dashoffset: -0.2; }
`;
// progress and done: the far dot fills and pops when the line reaches it.
const reach = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(1.3); }
  100% { transform: scale(1); }
`;
const draw = keyframes`
  from { stroke-dashoffset: 1; }
  to { stroke-dashoffset: 0; }
`;
const hit = keyframes`
  0%, 20% { transform: scale(1); }
  40% { transform: scale(1.4); }
  100% { transform: scale(1); }
`;
// failed: the line parts in the middle, each half springs back to its dot.
const part = keyframes`
  from { stroke-dashoffset: 0; }
  to { stroke-dashoffset: 0.82; }
`;
const shake = keyframes`
  0%, 20% { transform: translateX(0); }
  30% { transform: translateX(-0.9px); }
  40% { transform: translateX(0.9px); }
  50% { transform: translateX(-0.5px); }
  60%, 100% { transform: translateX(0); }
`;
// reconnecting: two halves reach for each other and never quite meet.
const reachOut = keyframes`
  from { stroke-dashoffset: 0.7; }
  to { stroke-dashoffset: 0.12; }
`;
const PenRoot = styled("span")({
    display: "inline-flex",
    flexShrink: 0,
    verticalAlign: "middle",
    "& svg": {
        display: "block",
        width: "100%",
        height: "100%",
        overflow: "visible"
    },
    "& .dot, & .lens, & .shake, & .thought line, & .spine": {
        transformBox: "fill-box",
        transformOrigin: "center"
    },
    "& .baton": {
        transformBox: "view-box",
        transformOrigin: "center"
    },
    "&.pen-loading": {
        "& .pen": {
            strokeDasharray: "0.4 1.6",
            animation: `${shuttle} ${LOADING_LEG}`
        },
        "& .dot-left": {
            animation: `${landLeft} ${LOADING_LEG}`
        },
        "& .dot-right": {
            animation: `${landRight} ${LOADING_LEG}`
        }
    },
    "&.pen-thinking": {
        "& .baton": {
            animation: `${turn} ${THINKING_CYCLE} ${IN_OUT} infinite`
        },
        "& .dot": {
            animation: `${tuck} ${THINKING_CYCLE} linear infinite`
        },
        "& .spine": {
            animation: `${tuckLine} ${THINKING_CYCLE} linear infinite`
        }
    },
    "&.pen-pondering": {
        "& .thought": {
            animation: `${fall} ${PONDERING_CYCLE} ${OUT} infinite`
        },
        "& .thought line": {
            opacity: 0,
            animation: `${absorb} ${PONDERING_CYCLE} linear infinite`
        },
        "& .thought-below, & .thought-below line": {
            animationDelay: "-0.9s"
        }
    },
    "&.pen-writing": {
        "& .word": {
            strokeDasharray: "1 1"
        },
        "& .word-1": {
            animation: `${writeWord(0, 14)} ${WRITING_CYCLE}`
        },
        "& .word-2": {
            animation: `${writeWord(16, 30)} ${WRITING_CYCLE}`
        },
        "& .word-3": {
            animation: `${writeWord(32, 44)} ${WRITING_CYCLE}`
        },
        "& .dot-right": {
            animation: `${wordWritten} ${WRITING_CYCLE}`
        }
    },
    "&.pen-reading": {
        "& .cursor": {
            strokeDasharray: "0.16 1.84",
            animation: `${readLine} 2.8s ${EXPO} infinite`
        }
    },
    "&.pen-syncing": {
        "& .pen": {
            strokeDasharray: "0.3 1.7",
            animation: `${pass} ${SYNCING_CYCLE}`
        },
        "& .dot": {
            animation: `${receive} ${SYNCING_CYCLE}`
        }
    },
    "&.pen-searching": {
        "& .lens": {
            animation: `${scan} 1.8s ${IN_OUT} infinite alternate`
        },
        "& .dot": {
            opacity: 0.75
        }
    },
    "&.pen-queued": {
        "& .waiting": {
            strokeDasharray: "0 0.1",
            animation: `${drift} 2.4s linear infinite`
        },
        "& .dot": {
            opacity: 0.5
        }
    },
    "&.pen-progress": {
        "& .fill": {
            strokeDasharray: "1 1",
            transition: `stroke-dashoffset 400ms ${EXPO}`
        },
        "& .target": {
            fillOpacity: 0
        },
        "& .target-reached": {
            fillOpacity: 1,
            animation: `${reach} 600ms ${EXPO}`
        }
    },
    "&.pen-done": {
        "& .pen": {
            strokeDasharray: "1 1",
            animation: `${draw} 600ms ${EXPO} both`
        },
        "& .dot": {
            animation: `${hit} ${ONE_SHOT}`
        }
    },
    "&.pen-failed": {
        "& .half": {
            strokeDasharray: "1 1",
            animation: `${part} 700ms ${EXPO} 150ms both`
        },
        "& .shake": {
            animation: `${shake} 900ms ${IN_OUT} both`
        }
    },
    "&.pen-reconnecting": {
        "& .half": {
            strokeDasharray: "1 1",
            animation: `${reachOut} 1.3s ${IN_OUT} infinite alternate`
        },
        "& .dot": {
            opacity: 0.65
        }
    },
    "@media (prefers-reduced-motion: reduce)": {
        "& *": {
            animation: "none !important",
            transition: "none !important"
        },
        "&.pen-loading .pen, &.pen-syncing .pen": {
            strokeDasharray: "none"
        },
        "&.pen-loading svg, &.pen-syncing svg, &.pen-thinking svg, &.pen-pondering svg, &.pen-writing svg, &.pen-reading svg, &.pen-searching svg, &.pen-reconnecting svg": {
            animation: `${breathe} 1.2s ease-in-out infinite alternate !important`
        }
    }
});
export default PenRoot;
